import {AlertTriangleIcon, ChevronDownIcon, TrendingUpIcon} from "lucide-react";
import {useMemo, useState} from "react";

import {buildComboChart, buildPieChart, SEMANTIC_COLORS} from "@components/charts/echarts-toolkit";
import {EChartsCard} from "@components/charts/EChartsCard";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {
    type BSSummaryData,
    type BSSummaryTrend,
    type PeriodOptions,
    type ThreeStatementCheck,
    useVNextBSSummary,
    useVNext3StatementForecast,
} from "@hooks/use-vnext";

import {ExecutiveSummary} from "./ExecutiveSummary";


interface BalanceSheetSummaryTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

function formatCurrency(value: number): string {
    return `$${Math.abs(value).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function formatCompact(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${value < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${value < 0 ? "-" : ""}$${(abs / 1_000).toFixed(0)}K`;
    return `${value < 0 ? "-" : ""}${formatCurrency(value)}`;
}

function formatRatio(value: number | null, suffix = ""): string {
    if (value == null || !Number.isFinite(value)) return "N/A";
    return `${value.toLocaleString(undefined, {maximumFractionDigits: 2})}${suffix}`;
}

function formatDelta(delta: number | null, deltaPct: number | null): string | null {
    if (delta == null && deltaPct == null) return null;
    const parts: string[] = [];
    if (delta != null) parts.push(formatCompact(delta));
    if (deltaPct != null) parts.push(`(${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(1)}%)`);
    return parts.join(" ");
}

interface ForecastBSMonth {
    month_key: string;
    label: string;
    status: string;
    rows: Array<{node_id: string; label: string; category_key: string; value: number}>;
}

interface ForecastBSBreakdown extends BSSummaryTrend {
    current_assets: number;
    non_current_assets: number;
    current_liabilities: number;
    non_current_liabilities: number;
}

function extractBsForecastTrends(forecastMonths: ForecastBSMonth[]): ForecastBSBreakdown[] {
    return forecastMonths
        .filter((m) => m.status === "forecast")
        .map((m) => {
            const v = (id: string) => m.rows.find((r) => r.node_id === id)?.value ?? 0;
            const totalAssets = v("__bs_total_assets__");
            const totalLiabEquity = v("__bs_total_liab_equity__");

            let totalLiabilities = 0;
            let totalEquity = 0;
            let currentAssets = 0;
            let nonCurrentAssets = 0;
            let currentLiab = 0;
            let nonCurrentLiab = 0;
            for (const r of m.rows) {
                if (r.node_id.startsWith("__")) continue;
                const ck = r.category_key || "";
                const top = ck.split(".")[0];
                if (top === "assets") {
                    if (ck.startsWith("assets.current")) currentAssets += r.value;
                    else nonCurrentAssets += r.value;
                } else if (top === "liabilities") {
                    totalLiabilities += r.value;
                    if (ck.startsWith("liabilities.current")) currentLiab += r.value;
                    else nonCurrentLiab += r.value;
                } else if (top === "equity") {
                    totalEquity += r.value;
                }
            }

            return {
                month_key: m.month_key,
                label: m.label,
                status: "forecast" as const,
                total_assets: totalAssets,
                total_liabilities: totalLiabilities || (totalLiabEquity - totalEquity),
                total_equity: totalEquity,
                net_assets: totalAssets - totalLiabilities,
                working_capital: currentAssets - currentLiab,
                current_assets: currentAssets,
                non_current_assets: nonCurrentAssets,
                current_liabilities: currentLiab,
                non_current_liabilities: nonCurrentLiab,
            };
        });
}

function openTab(tab: string) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: {tab}}));
}

export function BalanceSheetSummaryTab({
    workspaceId,
    scenarioId,
    actualMonths,
    forecastMonths = 0,
    period,
    selectedMonth,
    customFrom,
    customTo,
    compare,
    fyStartMonth,
    currentMonth,
}: BalanceSheetSummaryTabProps) {
    const [checksExpanded, setChecksExpanded] = useState(false);
    const periodOpts: PeriodOptions = {period, selectedMonth, customFrom, customTo, compare, fyStartMonth, currentMonth};
    const {data, isLoading: summaryLoading, error: summaryError} = useVNextBSSummary(workspaceId, scenarioId, forecastMonths, actualMonths, periodOpts);
    const {data: forecastData, isLoading: forecastLoading} = useVNext3StatementForecast(
        workspaceId, scenarioId, forecastMonths, actualMonths,
    );

    const isLoading = summaryLoading || forecastLoading;
    const error = summaryError;

    const forecastTrends = useMemo(
        () => forecastMonths > 0 ? extractBsForecastTrends(forecastData?.bs?.months ?? []) : [],
        [forecastData?.bs?.months, forecastMonths],
    );
    const forecastChecks = useMemo<ThreeStatementCheck[]>(
        () => (forecastData?.checks ?? []).filter((c) => c.key?.startsWith("bs")),
        [forecastData?.checks],
    );

    const trends = useMemo(() => {
        const actual = (data?.monthly_trends ?? []).filter((t) => t.status === "actual");
        return [...actual, ...forecastTrends];
    }, [data?.monthly_trends, forecastTrends]);

    const periods = useMemo(() => {
        const actualPeriods = (data?.bs_table?.periods ?? []).filter((p: {status: string}) => p.status === "actual");
        const forecastPeriods = forecastTrends.map((t) => ({month_key: t.month_key, label: t.label, status: "forecast"}));
        return [...actualPeriods, ...forecastPeriods];
    }, [data?.bs_table?.periods, forecastTrends]);

    const hasForecast = forecastTrends.length > 0;
    const firstForecast = forecastTrends[0]?.label;

    const forecastSectionTotals = useMemo(() => {
        const totals: Record<string, Record<string, number>> = {
            assets: {}, liabilities: {}, equity: {},
            current_assets: {}, non_current_assets: {},
            current_liabilities: {}, non_current_liabilities: {},
        };
        const netAssets: Record<string, number> = {};
        const balanceCheck: Record<string, number> = {};
        for (const t of forecastTrends) {
            totals.assets[t.month_key] = t.total_assets;
            totals.liabilities[t.month_key] = t.total_liabilities;
            totals.equity[t.month_key] = t.total_equity;
            totals.current_assets[t.month_key] = t.current_assets;
            totals.non_current_assets[t.month_key] = t.non_current_assets;
            totals.current_liabilities[t.month_key] = t.current_liabilities;
            totals.non_current_liabilities[t.month_key] = t.non_current_liabilities;
            netAssets[t.month_key] = t.net_assets;
            balanceCheck[t.month_key] = t.total_assets - (t.total_liabilities + t.total_equity);
        }
        return {totals, netAssets, balanceCheck};
    }, [forecastTrends]);

    // Capital Structure Trend — stacked bars for Assets/Liabilities + Net Assets line
    const capitalStructureOption = useMemo(() => {
        if (!trends.length) return null;
        return buildComboChart({
            categories: trends.map((t) => t.label),
            barSeries: [
                {
                    key: "assets",
                    label: "Total Assets",
                    data: trends.map((t) => t.total_assets),
                    color: SEMANTIC_COLORS.primary,
                    stack: "stack",
                },
                {
                    key: "liabilities",
                    label: "Total Liabilities",
                    data: trends.map((t) => -Math.abs(t.total_liabilities)),
                    color: "#94A3B8",
                    stack: "stack",
                },
            ],
            lineSeries: [
                {
                    key: "net_assets",
                    label: "Net Assets",
                    data: trends.map((t) => t.net_assets),
                    color: SEMANTIC_COLORS.positive,
                    yAxisIndex: 1,
                },
            ],
            yFormat: "compact_currency",
            y2Format: "compact_currency",
            forecastStart: firstForecast,
        });
    }, [firstForecast, trends]);

    // Asset Composition pie
    const assetPieOption = useMemo(() => {
        const composition = data?.composition?.assets ?? [];
        if (!composition.length) return null;
        const option = buildPieChart({
            segments: composition.slice(0, 8).map((c) => ({name: c.category, value: Math.abs(c.value)})),
            showLabels: true,
        });
        // Position pie with room for scrollable legend at bottom
        const series = option.series as Record<string, unknown>[];
        series[0].center = ["50%", "42%"];
        series[0].radius = "60%";
        (option as Record<string, unknown>).legend = {
            orient: "horizontal" as const,
            bottom: 0,
            left: "center",
            type: "scroll",
            textStyle: {fontSize: 11},
            pageTextStyle: {color: "#64748B"},
        };
        return option;
    }, [data?.composition?.assets]);

    if (isLoading) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-48 w-full rounded-xl" />
                <Skeleton className="h-64 w-full rounded-xl" />
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">{error}</CardContent>
            </Card>
        );
    }

    if (!data || !data.kpi_cards.length) {
        return (
            <Card>
                <CardContent className="p-10 text-center text-muted-foreground">
                    No balance sheet summary data available yet.
                </CardContent>
            </Card>
        );
    }

    const kpiCards = data.kpi_cards;
    const keyRatios = data.key_ratios;
    const sections = data.bs_table.sections;
    const insights = data.insights;

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#F8FAFC] p-4 pb-8">
            <div className="mx-auto w-full max-w-[1400px] space-y-6" data-tour="dashboard-bs-tab">
                {/* Header */}
                <header className="rounded-2xl bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[#CCFFF2]">
                                Balance Sheet | Statement of Financial Position
                            </p>
                            <h2 className="mt-2 text-2xl font-extrabold text-white">Balance Sheet Summary</h2>
                            <p className="mt-1 text-sm text-white/85">
                                {hasForecast
                                    ? `Actuals from Xero · Forecast from 3-statement engine${data.meta.period_label ? ` — ${data.meta.period_label}` : ""}`
                                    : data.meta.period_label ? `${data.meta.period_label} — Actuals only` : "Actuals only — sourced from Xero Balance Sheet snapshots."}
                            </p>
                            <p className="mt-2 text-xs text-white/80">
                                Summary is read-only. Use Forecast Summary for full statement detail and forecast edits.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="secondary"
                            className="shrink-0 bg-white text-[#0F172A] hover:bg-white/90"
                            onClick={() => openTab("forecast_summary")}
                        >
                            Open Forecast Summary
                        </Button>
                    </div>
                </header>

                <ExecutiveSummary workspaceId={workspaceId} scenarioId={scenarioId} tabKey="bs_summary" period={period} selectedMonth={selectedMonth} customFrom={customFrom} customTo={customTo} fyStartMonth={fyStartMonth} currentMonth={currentMonth} />

                {/* KPI Cards */}
                <section>
                    <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Key Metrics</h3>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        {kpiCards.map((card) => (
                            <KPICard key={card.key} card={card} />
                        ))}
                    </div>
                </section>

                {/* Charts */}
                <div className="grid gap-6 xl:grid-cols-2">
                    {capitalStructureOption && (
                        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                            <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                                Capital Structure Trend
                            </h3>
                            <EChartsCard option={capitalStructureOption} height={300} />
                        </section>
                    )}
                    {assetPieOption && (
                        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                            <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                                Asset Composition
                            </h3>
                            <EChartsCard option={assetPieOption} height={300} />
                        </section>
                    )}
                </div>

                {/* Key Ratios */}
                {keyRatios.length > 0 && (
                    <section>
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Key Ratios</h3>
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {keyRatios.map((ratio) => (
                                <div
                                    key={ratio.key}
                                    className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm"
                                >
                                    <p className="text-2xl font-extrabold tabular-nums text-[#209DB3]">
                                        {ratio.key === "working_capital"
                                            ? formatCompact(ratio.value ?? 0)
                                            : ratio.key === "equity_ratio" || ratio.key === "net_asset_growth"
                                              ? formatRatio(ratio.value, "%")
                                              : formatRatio(ratio.value, "x")}
                                    </p>
                                    <p className="mt-1 text-xs font-medium text-[#0F172A]">{ratio.label}</p>
                                    <p className="mt-0.5 text-[10px] text-muted-foreground">{ratio.calculation}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Condensed BS Table — section totals only */}
                {sections.length > 0 && periods.length > 0 && (
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-tour="dashboard-bs-overview">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                            Balance Sheet Overview
                        </h3>
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-[#0F172A] text-white">
                                        <th className="px-3 py-2 text-left min-w-[200px]" />
                                        {periods.map((p) => (
                                            <th key={p.month_key} className={`px-3 py-2 text-right min-w-[100px] ${p.status === "forecast" ? "bg-amber-900/30" : ""}`}>
                                                {p.label}
                                                <span className="ml-1 text-[10px] opacity-60">
                                                    {p.status === "forecast" ? "(F)" : "(actual)"}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sections.map((section) => (
                                        <tr key={section.key} className="border-b border-gray-200 font-semibold">
                                            <td className="px-3 py-2.5 text-[#0F172A]">Total {section.label}</td>
                                            {periods.map((p) => {
                                                const isForecast = p.status === "forecast";
                                                const val = isForecast
                                                    ? (forecastSectionTotals.totals[section.key]?.[p.month_key] ?? 0)
                                                    : (section.total[p.month_key] ?? 0);
                                                return (
                                                    <td key={p.month_key} className={`px-3 py-2.5 text-right tabular-nums text-[#0F172A] ${isForecast ? "bg-amber-50" : ""}`}>
                                                        {formatCurrency(val)}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {/* Net Assets row */}
                                    <tr className="border-t-2 border-[#209DB3] bg-[#F0FDFA] font-bold">
                                        <td className="px-3 py-2.5 text-[#0F172A]">Net Assets</td>
                                        {periods.map((p) => {
                                            const isForecast = p.status === "forecast";
                                            const val = isForecast
                                                ? (forecastSectionTotals.netAssets[p.month_key] ?? 0)
                                                : (data.bs_table.net_assets[p.month_key] ?? 0);
                                            return (
                                                <td key={p.month_key} className={`px-3 py-2.5 text-right tabular-nums text-[#0F172A] ${isForecast ? "bg-amber-50" : ""}`}>
                                                    {formatCurrency(val)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Balance Check row */}
                                    <tr className="text-muted-foreground">
                                        <td className="px-3 py-2 text-[10px]">Balance Check (should be 0)</td>
                                        {periods.map((p) => {
                                            const isForecast = p.status === "forecast";
                                            const val = isForecast
                                                ? (forecastSectionTotals.balanceCheck[p.month_key] ?? 0)
                                                : (data.bs_table.balance_check[p.month_key] ?? 0);
                                            return (
                                                <td
                                                    key={p.month_key}
                                                    className={`px-3 py-2 text-right tabular-nums text-[10px] ${isForecast ? "bg-amber-50" : ""} ${Math.abs(val) > 0.01 ? "text-red-600 font-semibold" : ""}`}
                                                >
                                                    {val.toFixed(2)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {/* Insights */}
                {insights.length > 0 && (
                    <section className="space-y-3">
                        <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Insights</h3>
                        {insights.map((note, idx) => (
                            <div
                                key={`${note.type}-${idx}`}
                                className={`rounded-r-2xl border-l-4 p-4 text-sm ${
                                    note.type === "insight"
                                        ? "border-l-[#209DB3] bg-[#F0FDFA] text-[#0F172A]"
                                        : "border-l-[#FF5100] bg-[#FFF7ED] text-[#7C2D12]"
                                }`}
                            >
                                <div className="flex items-start gap-2">
                                    {note.type === "insight" ? (
                                        <TrendingUpIcon className="mt-0.5 size-4 text-[#209DB3]" />
                                    ) : (
                                        <AlertTriangleIcon className="mt-0.5 size-4 text-[#FF5100]" />
                                    )}
                                    <div>
                                        <p className="font-semibold">{note.title}</p>
                                        <p className="mt-0.5 text-xs opacity-80">{note.text}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </section>
                )}

                {/* Engine Checks */}
                {forecastChecks.length > 0 && (
                    <section className="space-y-3">
                        <button
                            type="button"
                            className="flex w-full items-center justify-between gap-3 text-left"
                            onClick={() => setChecksExpanded((value) => !value)}
                            aria-expanded={checksExpanded}
                            aria-controls="bs-engine-checks-panel"
                        >
                            <div>
                                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Engine Checks</h3>
                                <p className="mt-1 text-xs text-muted-foreground">Hidden by default on summary tabs. Expand to inspect balance and rollforward diagnostics.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{checksExpanded ? "Hide" : "Show"} {forecastChecks.length}</span>
                                <ChevronDownIcon className={`size-4 text-muted-foreground transition-transform ${checksExpanded ? "rotate-180" : ""}`} />
                            </div>
                        </button>
                        {checksExpanded && (
                            <div id="bs-engine-checks-panel" className="space-y-3">
                                {forecastChecks.map((chk) => (
                                    <div
                                        key={chk.key}
                                        className={`rounded-r-2xl border-l-4 p-4 text-sm ${
                                            chk.status === "PASS"
                                                ? "border-l-green-500 bg-green-50 text-green-900"
                                                : chk.status === "WARN"
                                                  ? "border-l-amber-500 bg-amber-50 text-amber-900"
                                                  : "border-l-red-500 bg-red-50 text-red-900"
                                        }`}
                                    >
                                        <p className="font-semibold">{chk.label}</p>
                                        {chk.details && <p className="mt-0.5 text-xs opacity-80">{chk.details}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <p className="text-center text-[10px] text-muted-foreground">
                    {hasForecast
                        ? `Actuals from Xero · Forecast from 3-statement engine. Generated ${new Date(data.meta.generated_at).toLocaleString()}.`
                        : `Data sourced from Xero via VNext engine. Generated ${new Date(data.meta.generated_at).toLocaleString()}.`}
                </p>
            </div>
        </div>
    );
}

// ── Sub-components ──────────────────────────────────────────────

const BS_UP_IS_BAD = new Set(["total_liabilities"]);

function KPICard({card}: {card: BSSummaryData["kpi_cards"][number]}) {
    const deltaStr = formatDelta(card.delta, card.delta_pct);
    const delta = card.delta ?? 0;
    const isPositive = delta >= 0;
    const inverted = BS_UP_IS_BAD.has(card.key) || card.key.includes("liabilit");
    const isGood = inverted ? !isPositive : isPositive;
    const isRatio = card.key === "current_ratio";

    return (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 text-center shadow-sm">
            <p className="text-2xl font-extrabold tabular-nums text-[#209DB3]">
                {isRatio ? formatRatio(card.value, "x") : formatCompact(card.value ?? 0)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{card.label}</p>
            {deltaStr && (
                <p className={`mt-1 text-[10px] font-medium ${isGood ? "text-green-600" : "text-red-600"}`}>
                    {deltaStr} {card.vs_label}
                </p>
            )}
        </div>
    );
}
